import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { FormikProvider, useFormik } from 'formik'

import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { ManualTransactionModel } from '@/reducers/admin/allTransactions.slice'
import { getUserBooksLockedForUserYear } from '@/reducers/auth/user.selectors'

import {
  getFieldName,
  makeDateSchema,
  makeNumberSchema,
  makeReqStringSchema,
} from '@/componentsV1/BaseComponents'
import { UploadDocumentType } from '@/constants/businessConstants'
import { TypographyStyles } from '@/styles'
import { LockedBooksMessage } from '@/features/transactions/LockedBooksMessage'
import { createSingleManualTransaction } from '@/features/transactions/transactions.slice'
import { UPLOAD_COPY } from '@/features/transactions/copyConstants'
import { TransactionTypes } from '@/features/transactions/constants'
import { UploadSuccessPayload } from '@/features/transactions/Upload/Module'
import { DisplayFile } from '@/features/transactions/lists/TransactionReceiptsPanel'
import { destroySingleUserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

// TODO: Move to v2
import { UploadedFile } from '@/componentsV1/FileUpload/FileUploadModal'
import { userUploadDocument } from '@/actions/awsActions'
import { getCurrentUser } from '@/selectors/user.selectors'

import { Alert, Button, FileInput, Form, List, Separator, Text } from '@/ui'

export interface AddTransactionProps {
  onUploadComplete?: (_payload: {
    success: boolean
    data?: UploadSuccessPayload[]
  }) => void
  closeFlyout: () => void
  transactionType: string
}

export const AddTransaction = ({
  transactionType = TransactionTypes.DEFAULT,
  onUploadComplete,
  closeFlyout,
}: AddTransactionProps) => {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile>()
  const [error, setError] = useState('')
  const currentDate = DateTime.local().toFormat(DATE_FORMATS_LUXON.INPUT)
  const booksLockedForUserYear = useReselector(getUserBooksLockedForUserYear)
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const priorYear = DateTime.local().minus({ years: 1 }).year
  const currentYear = DateTime.local().year
  const minDate = DateTime.fromISO(`${priorYear}-01-01`).toISODate()

  const deleteFile = async (fileId: number) => {
    await destroySingleUserDocument(fileId)(dispatch)
  }

  const formik = useFormik({
    initialValues: {
      date: currentDate,
      amountInDollars:
        transactionType === TransactionTypes.INCOME ? '0.00' : '-0.00',
      description: '',
      receipt: null as UploadedFile | null,
    },
    enableReinitialize: true,
    onSubmit: async ({ amountInDollars, date, description, receipt }) => {
      const manualTransaction: Omit<ManualTransactionModel, 'receipts'> & {
        receipt?: UploadedFile | null
      } = {
        amountInDollars,
        date,
        description: description.trim(),
        receipt,
      }

      try {
        const result =
          await createSingleManualTransaction(manualTransaction)(dispatch)

        if (result) {
          onUploadComplete?.({
            success: true,
            data: [{ ...manualTransaction, id: result?.id }],
          })
        } else {
          setError(UPLOAD_COPY.SINGLE_TRANSACTION_ERROR_MESSAGE)
          return
        }

        closeFlyout()
      } catch (error) {
        console.error(error)
      }
    },
  })

  const { isSubmitting, isValid, submitForm, setFieldValue } = formik

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]

    if (!file) {
      setError('Please select a file to upload.')
      return
    }

    if (!user) {
      setError('User not found. Please try again.')
      return
    }

    try {
      const payload = {
        file,
        user: { id: user.id, uuid: user.uuid },
        documentType: UploadDocumentType.RECEIPT,
      }

      const docRes = await userUploadDocument(payload)(dispatch)

      if (docRes && 'userDocument' in docRes) {
        const userDoc = docRes.userDocument
        const uploadedFile: UploadedFile = {
          title: userDoc.fileDescription || '',
          url: userDoc.signedUrl || '',
          id: userDoc.id,
          fileName: file.name,
          doc: userDoc,
        }

        setUploadedFile(uploadedFile)
        setFieldValue(
          getFieldName<typeof formik.values>('receipt'),
          uploadedFile
        )
      } else {
        setError('Failed to upload file. Please try again.')
      }
    } catch (error) {
      console.error('File upload error:', error)
      setError('Failed to upload file. Please try again.')
    }
  }

  useEffect(() => {
    setError('')
  }, [formik.values])

  return (
    <FormikProvider value={formik}>
      {booksLockedForUserYear && (
        <LockedBooksMessage year={booksLockedForUserYear} />
      )}

      {/* // TODO Formik Date Input */}
      <Form.DateInput
        className="p-2"
        clearable
        required
        fullWidth
        label="Date"
        minDate={new Date(priorYear, 0, 1)}
        maxDate={new Date()}
        placeholder={DATE_FORMATS.INPUT}
        name={getFieldName<typeof formik.values>('date')}
        schema={makeDateSchema({
          field: 'this transaction',
          minDate: minDate || '',
          maxDate: DateTime.now().toISO(),
          rangeError: `You can only add transactions from ${priorYear}-${currentYear}.`,
        })}
      />

      <Text variant={TypographyStyles.BODY_MD} className="pt-6 mb-2">
        {transactionType} Amount
      </Text>

      <Form.Input
        required
        className="w-full"
        name={getFieldName<typeof formik.values>('amountInDollars')}
        componentType="currency"
        onChange={(value) => {
          if (transactionType === TransactionTypes.EXPENSE) {
            if (value === '' || value === '0.00') {
              formik.setFieldValue('amountInDollars', '-0.00')
            }
            if (Number(value) > 0) {
              formik.setFieldValue('amountInDollars', `-${value}`)
            }
          } else {
            if (value === '' || value === '-0.00') {
              formik.setFieldValue('amountInDollars', '0.00')
            }
            if (value && Number(value) < 0) {
              formik.setFieldValue('amountInDollars', Number(value) * -1)
            }
          }
        }}
        schema={makeNumberSchema({
          field: transactionType,
          allowedDecimals: 2,
          transactionNotZero: true,
          allowNegative: transactionType === TransactionTypes.EXPENSE,
          maxNumber:
            transactionType === TransactionTypes.EXPENSE ? -0.01 : undefined,
          minNumber:
            transactionType === TransactionTypes.INCOME ? 0.01 : undefined,
        })}
      />

      <Text variant={TypographyStyles.BODY_MD} className="pt-6 mb-2">
        Description
      </Text>

      <Form.Textarea
        required
        className="w-full"
        name={getFieldName<typeof formik.values>('description')}
        placeholder="e.g. USPS Withdrawal"
        schema={makeReqStringSchema({
          field: 'description',
        })}
      />

      {transactionType === TransactionTypes.EXPENSE && (
        <>
          <Text variant={TypographyStyles.BODY_MD} className="pt-6">
            Receipt
          </Text>
          {uploadedFile && (
            <List className="list-disc mb-4 ml-4">
              <DisplayFile
                key={uploadedFile.id}
                receipt={uploadedFile}
                deleteFile={deleteFile}
              />
            </List>
          )}

          <FileInput label="Upload" onChange={handleFileChange} />
        </>
      )}

      {error && (
        <Alert variant="error" key={error}>
          {error}
        </Alert>
      )}

      <Separator className="my-6" />

      <div className="flex justify-end">
        <Button
          className="m-4 ml-0"
          variant="link"
          onClick={() => closeFlyout()}
        >
          Cancel
        </Button>

        <Button
          className="m-4"
          onClick={submitForm}
          disabled={!isValid || isSubmitting}
          aria-disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        >
          Add {transactionType}
        </Button>
      </div>
    </FormikProvider>
  )
}
